import React, {useState} from 'react';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {InputAdornment, Stack, TextField} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
  Icon,
  SwitchButton,
  Tooltip,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {
  CONTENT_STUDIO_PRICING_SOURCES,
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import {SESSION_ID_NOT_FOUND} from '@/lib/delta/deltaConstants.js';
import {handleContentStudioError} from '@/utils/contentStudioErrorHandling';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import {calculateVideoTokensForAssets} from '@/views/content-studio/components/utils';
import {FEATURE_ID} from '@/views/content-studio/constants';
import useOpenPricing, {
  OPEN_PRICING_TYPE,
} from '@/views/content-studio/hooks/use-open-pricing';
import resolveCreditAccessAction, {
  CREDIT_ACCESS_ACTION,
} from '@/views/content-studio/utils/resolveCreditAccessAction';
import useCreateDubbingProject from '@/hooks/content-studio/mutations/use-create-dubbing-project';
import useDubbingLanguagesQuery from '@/hooks/content-studio/use-dubbing-languages-query';
import useNavigation from '@/hooks/use-navigation';
import contentStudioStore, {
  contentStudioActions,
  ErrorKind,
} from '@/stores/contentStudioStore';
import FeaturePage from '../feature-page/FeaturePage';
import ManyToManyEditor from '../many-to-many-editor/ManyToManyEditor';
import ManyToManySubmitButton from '../many-to-many-editor/ManyToManySubmitButton';
import ManyToManyOptionDisplay from '../many-to-many-option-display/ManyToManyOptionDisplay';
import MultiLineDropdownTitle from '../multi-line-dropdown/MultiLineDropdownTitle';
import MultilineDropDownButton from '../multi-line-dropdown/MultilineDropDownButton';
import {useDefaultProjectName} from '../projects/utils';

function DubbingPage() {
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [projectName, setProjectName] = useState(
    useDefaultProjectName(translate('Dub'))
  );
  const [enableLipSync, setEnableLipSync] = useState(true);
  const {data: languages} = useDubbingLanguagesQuery();
  const {selectedAssets} = contentStudioStore(
    useShallow((state) => ({
      selectedAssets: state.selectedAssets,
    }))
  );
  const {uiInteraction} = useUiInteractionEvent();
  const {navigate} = useNavigation();
  const openPricing = useOpenPricing();
  const {mutateAsync: createDubbingProject} = useCreateDubbingProject();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isUploading = selectedAssets.some((asset) => asset.isUploading);

  const handleTranslateClick = async () => {
    setIsSubmitting(true);
    try {
      const requiredCredits =
        calculateVideoTokensForAssets(selectedAssets) *
        selectedLanguages.length;
      const creditAccessAction =
        await resolveCreditAccessAction(requiredCredits);

      if (creditAccessAction !== CREDIT_ACCESS_ACTION.NO_OP) {
        const openType =
          creditAccessAction === CREDIT_ACCESS_ACTION.REDIRECT_TO_PRICING
            ? OPEN_PRICING_TYPE.REDIRECT
            : OPEN_PRICING_TYPE.MODAL;
        openPricing(openType, CONTENT_STUDIO_PRICING_SOURCES.EDITOR_GENERATE);
        setIsSubmitting(false);
        return;
      }

      const interactionId = uuid();
      const sessionId = AnalyticsService.getSessionId() || SESSION_ID_NOT_FOUND;

      uiInteraction({
        item_name: 'translate',
        interaction_location: CONTENT_STUDIO_VIEW_NAMES.TRANSLATE_EDITOR,
        flow_name: FLOW_NAMES.CONTENT_STUDIO.TRANSLATE,
        interaction_id: interactionId,
      });

      await createDubbingProject({
        asset_ids: selectedAssets.map((asset) => asset.id),
        project_name: projectName,
        languages: selectedLanguages,
        enable_lip_sync: enableLipSync,
        analytics_params: {
          interaction_id: interactionId,
          session_id: sessionId,
        },
      });
      contentStudioActions.setSelectedAssets([]);
      contentStudioActions.setIsProjectShelfOpen(true);
      navigate(ROUTES.CONTENT_LAB);
    } catch (error) {
      handleContentStudioError(
        error,
        ErrorKind.FAILURE,
        'Failed to create dubbing project'
      );
    }
    setIsSubmitting(false);
  };

  return (
    <FeaturePage featureId={FEATURE_ID.DUB}>
      <ManyToManyEditor
        flowName={FLOW_NAMES.CONTENT_STUDIO.TRANSLATE}
        asset_page_route={ROUTES.CONTENT_LAB_TRANSLATE_ASSETS}
        isLoading={isSubmitting}
      >
        <Stack
          paddingX="36px"
          paddingTop="24px"
          direction="column"
          height="100%"
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            marginBottom="8px"
          >
            <Headline size="xl">
              {translate('Dub to multiple languages')}
            </Headline>
            <Icon
              name="Navigation-Messages"
              appearance="primary"
              size="large"
            />
          </Stack>
          <Body size="lg" marginBottom="42px">
            {translate('Translate and dub content to reach new markets')}
          </Body>
          <Stack
            direction="row"
            marginBottom="40px"
            justifyContent="space-between"
          >
            <MultilineDropDownButton
              label={translate('Choose languages')}
              options={(languages || [])
                .filter((lang) =>
                  lang?.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((lang) => ({
                  id: lang,
                  label: lang,
                }))}
              onChange={(option) => {
                if (selectedLanguages.includes(option.id)) {
                  setSelectedLanguages(
                    selectedLanguages.filter((lang) => lang !== option.id)
                  );
                } else {
                  setSelectedLanguages([...selectedLanguages, option.id]);
                }
              }}
              selectedOptionIds={selectedLanguages}
              horizontalScroll
              renderMenuTitle={() => (
                <MultiLineDropdownTitle
                  label={translate('Search languages')}
                  value={searchQuery}
                  onChangeSearch={(event) => {
                    setSearchQuery(event.target.value);
                  }}
                />
              )}
            />
            <Tooltip
              title={translate(
                'Sync translated audio with mouth movements. Turn off for voiceover‒only videos to save processing time.'
              )}
              arrow
              type="light"
              placement="top"
              sx={{
                width: '182px',
                padding: '10px 12px',
                textAlign: 'center',
                fontSize: '14px',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <SwitchButton
                  label={translate('Lip sync')}
                  checked={enableLipSync}
                  checkedTrackColor={designSystemToken('semantic.bg.neutral')}
                  onChange={(event, checked) => {
                    setEnableLipSync(checked);
                    uiInteraction({
                      item_name: 'lip_sync_toggle',
                      interaction_location:
                        CONTENT_STUDIO_VIEW_NAMES.TRANSLATE_EDITOR,
                      flow_name: FLOW_NAMES.CONTENT_STUDIO.TRANSLATE,
                      interaction_details: checked ? 'on' : 'off',
                    });
                  }}
                />
                <Icon
                  name="Actions-Info-Line"
                  appearance="primary"
                  size="small"
                />
              </Stack>
            </Tooltip>
          </Stack>
          <ManyToManyOptionDisplay
            options={selectedLanguages.map((lang) => ({
              id: lang,
              label: lang,
            }))}
            onDelete={(option) => {
              setSelectedLanguages(
                selectedLanguages.filter((lang) => lang !== option)
              );
            }}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            marginTop="auto"
          >
            <TextField
              label={translate('Insert project name')}
              placeholder={translate('Enter a project name')}
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              sx={{marginBottom: '60px'}}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon
                      name="Navigation-Edit"
                      size="medium"
                      appearance="primary"
                    />
                  </InputAdornment>
                ),
              }}
            />
            <ManyToManySubmitButton
              selectedAssets={selectedAssets}
              selectedOptions={selectedLanguages.length}
              verb="Dub"
              noun="Language"
              iconName="Navigation-Messages"
              onClick={handleTranslateClick}
              disabled={
                selectedAssets.length === 0 ||
                isUploading ||
                selectedLanguages.length === 0 ||
                isSubmitting
              }
              isLoading={isSubmitting || isUploading}
              bottonTooltip={
                isUploading
                  ? translate('files-uploading-tooltip', {featureName: 'Dub'})
                  : ''
              }
            />
          </Stack>
        </Stack>
      </ManyToManyEditor>
    </FeaturePage>
  );
}

export default DubbingPage;
